import React, { useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';

/**
 * PaymentReceipt — printable receipt for a payment received.
 * Also shows QR code for remaining balance if any.
 *
 * Props:
 *   payment       — { _id, amount, mode, date, voucherNumber, referenceNumber, notes }
 *   patient       — { name, phone, uhid }
 *   outstanding   — { billed, paid, refunded, outstanding } (balance AFTER this payment)
 *   clinic        — { name, address, phone, email, gstin, upiId }
 *   onClose
 */
const PaymentReceipt = ({ payment, patient, outstanding, clinic, onClose }) => {
  const printRef = useRef(null);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);

  const fmtINR = n => (n || 0).toLocaleString('en-IN');
  const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  const modeLabels = { cash:'Cash', card:'Card', upi:'UPI', cheque:'Cheque', 'bank-transfer':'Bank Transfer', insurance:'Insurance', other:'Other' };

  const remaining = outstanding?.outstanding || 0;
  const upiId = clinic?.upiId || '';
  const paidAmount = payment.amount || 0;

  // QR for the amount received in THIS payment
  const paidUpiUrl = upiId && paidAmount > 0
    ? `upi://pay?pa=${upiId}&pn=${encodeURIComponent(clinic.name || 'Siddharth Dental')}&am=${paidAmount.toFixed(2)}&cu=INR&tn=${encodeURIComponent(`Payment ${payment.voucherNumber || ''} - ${patient.name}`)}`
    : '';

  // Number to words
  const numberToWords = (num) => {
    if (num === 0) return 'Zero';
    const a = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten',
               'Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
    const b = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
    const g = ['','Thousand','Lakh','Crore'];
    const toW = (n) => {
      if (n === 0) return '';
      if (n < 20) return a[n] + ' ';
      if (n < 100) return b[Math.floor(n/10)] + ' ' + a[n%10] + ' ';
      return a[Math.floor(n/100)] + ' Hundred ' + toW(n%100);
    };
    num = Math.round(Math.abs(num));
    const chunks = [];
    chunks.push(num % 1000); num = Math.floor(num / 1000);
    while (num > 0) { chunks.push(num % 100); num = Math.floor(num / 100); }
    let result = '';
    for (let i = chunks.length - 1; i >= 0; i--) {
      if (chunks[i] > 0) result += toW(chunks[i]) + g[i] + ' ';
    }
    return result.trim() || 'Zero';
  };

  // Grab QR SVG from the on-screen preview (already rendered by React)
  const getRenderedQrSvg = (refId) => {
    const el = document.getElementById(refId);
    if (!el) return '';
    const svg = el.querySelector('svg');
    if (!svg) return '';
    return svg.outerHTML;
  };

  const generateReceiptHtml = (copyLabel, paidQrSvg) => {
    const cn = clinic?.name || 'SIDDHARTH DENTAL HOSPITAL';
    const ca = clinic?.address || 'NEAR SIDDHARTH COMPUTER, FULIYA GATE ROAD, SHAHPURA, RAJASTHAN 311404';
    const cp = clinic?.phone || '+91 9685058003';
    return `<div style="padding:8px 10px;border:1px solid #333">
      <div style="display:flex;justify-content:space-between;margin-bottom:6px">
        <div style="font-size:13px;font-weight:bold;letter-spacing:3px">PAYMENT RECEIPT</div>
        <div style="font-size:8px;color:#666;letter-spacing:1px;text-transform:uppercase">${copyLabel}</div>
      </div>
      <div style="text-align:center;border-bottom:1px solid #333;padding-bottom:8px;margin-bottom:8px">
        <div style="font-size:15px;font-weight:bold">${cn}</div>
        <div style="font-size:8px;color:#444">${ca}</div>
        <div style="font-size:8px"><strong>Mobile:</strong> ${cp}</div>
      </div>
      <div style="display:flex;justify-content:space-between;margin-bottom:8px;font-size:10px">
        <div style="border:1px solid #ccc;padding:5px 8px;flex:1;margin-right:16px">
          <div style="font-size:8px;color:#888">Patient Details:</div>
          <div style="font-weight:bold;font-size:11px">${patient.name}</div>
          ${patient.phone ? `<div style="font-size:9px">Ph: ${patient.phone}</div>` : ''}
          ${patient.uhid ? `<div style="font-size:9px">UHID: ${patient.uhid}</div>` : ''}
        </div>
        <div style="text-align:right;font-size:10px">
          <div><strong>Receipt No:</strong> ${payment.voucherNumber || ''}</div>
          <div><strong>Date:</strong> ${fmtDate(payment.date)}</div>
        </div>
      </div>
      <table style="width:100%;border-collapse:collapse">
        <thead><tr style="background:#f0f0f0"><th style="border:1px solid #999;padding:4px 6px;font-size:9px;text-transform:uppercase;text-align:left">Description</th><th style="border:1px solid #999;padding:4px 6px;font-size:9px;text-transform:uppercase;text-align:left">Mode</th><th style="border:1px solid #999;padding:4px 6px;font-size:9px;text-transform:uppercase;text-align:left">Reference</th><th style="border:1px solid #999;padding:4px 6px;font-size:9px;text-transform:uppercase;text-align:right">Amount</th></tr></thead>
        <tbody><tr><td style="border:1px solid #ccc;padding:4px 6px">Payment Received</td><td style="border:1px solid #ccc;padding:4px 6px">${modeLabels[payment.mode] || payment.mode}</td><td style="border:1px solid #ccc;padding:4px 6px">${payment.referenceNumber || '—'}</td><td style="border:1px solid #ccc;padding:4px 6px;text-align:right;font-weight:bold;font-size:12px">₹${fmtINR(payment.amount)}</td></tr></tbody>
      </table>
      <div style="font-size:8px;border:1px solid #ccc;border-top:none;padding:3px 6px">Amount Received (in words): <strong>INR ${numberToWords(payment.amount)} Rupees Only</strong></div>
      <div style="background:#f8f8f8;border:1px solid #ddd;padding:6px 10px;margin:8px 0;font-size:9px">
        <div style="display:flex;justify-content:space-between;margin-bottom:2px"><span>Total Billed:</span><span>₹${fmtINR(outstanding?.billed)}</span></div>
        <div style="display:flex;justify-content:space-between;margin-bottom:2px;color:#10b981"><span>Total Paid:</span><span>₹${fmtINR(outstanding?.paid)}</span></div>
        <div style="display:flex;justify-content:space-between;font-weight:bold;padding-top:3px;border-top:1px solid #ddd;color:${remaining > 0 ? '#dc2626' : '#10b981'}"><span>Balance Due:</span><span>₹${fmtINR(remaining)}</span></div>
      </div>
      <div style="text-align:right;font-size:11px;font-weight:bold;margin-top:4px">Amount Received: ₹${fmtINR(paidAmount)}</div>
      <div style="display:flex;justify-content:space-between;align-items:flex-end;margin-top:12px">
        <div style="text-align:center">
          ${paidQrSvg ? `<div style="width:70px;height:70px;margin:0 auto">${paidQrSvg}</div>` : ''}
          <div style="font-size:7px;color:#333;font-weight:bold;margin-top:2px">₹${fmtINR(paidAmount)} Received</div>
          <div style="border-top:1px solid #333;margin-top:10px;padding-top:3px;font-size:8px;width:130px">Receiver's Signature</div>
        </div>
        <div style="text-align:right">
          <div style="font-size:8px">For ${cn}</div>
          <div style="font-size:9px;font-weight:bold;margin-top:12px">${cn}</div>
          <div style="font-size:8px">Shahpura Mo.${cp}</div>
          <div style="border-top:1px solid #333;margin-top:4px;padding-top:3px;font-size:8px">Authorized Signatory</div>
        </div>
      </div>
      <div style="text-align:left;font-size:6px;color:#aaa;margin-top:8px">Page 1/1 • Computer-generated receipt.</div>
    </div>`;
  };

  const handlePrint = () => {
    const copies = Number(localStorage.getItem('dentalBillCopies') || '1');
    const isTwo = copies >= 2;
    const gap = Number(localStorage.getItem('dentalBillGap')||'15');

    // Grab QR SVG from the on-screen preview
    const paidQrSvg = getRenderedQrSvg('receipt-qr-paid');

    const html1 = generateReceiptHtml('ORIGINAL', paidQrSvg);
    const html2 = isTwo ? generateReceiptHtml('DUPLICATE', paidQrSvg) : '';

    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Receipt ${payment.voucherNumber}</title>
      <style>
        @page { size: A4; margin: ${isTwo ? '8mm' : '12mm'}; }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: Arial, sans-serif; color: #222; }
        @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
      </style></head><body>
      ${html1}
      ${isTwo ? `<div style="height:${gap}mm;display:flex;align-items:center"><div style="flex:1;border-top:1px dashed #999"></div><span style="padding:0 8px;font-size:7px;color:#aaa">✂ CUT HERE</span><div style="flex:1;border-top:1px dashed #999"></div></div>${html2}` : ''}
      <script>window.onload=function(){window.print();window.close();}<\/script>
      </body></html>`);
    w.document.close();
  };

  // Auto-hide success after 4 seconds
  const handleConfirm = () => {
    setPaymentConfirmed(true);
    setTimeout(() => setPaymentConfirmed(false), 4000);
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
      backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 3000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: 'white', borderRadius: '12px',
          width: '650px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          position: 'relative',
        }}>

        {/* ── SUCCESS OVERLAY ── */}
        {paymentConfirmed && (
          <div style={{
            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
            backgroundColor: 'rgba(255,255,255,0.97)', zIndex: 20,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            borderRadius: '12px',
            animation: 'fadeIn 0.3s ease',
          }}>
            <div style={{
              width: '100px', height: '100px', borderRadius: '50%',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 8px 30px rgba(16,185,129,0.4)',
              animation: 'popIn 0.4s ease',
              marginBottom: '20px',
            }}>
              <span style={{ fontSize: '3rem', color: 'white', lineHeight: 1 }}>✓</span>
            </div>
            <h2 style={{ margin: '0 0 8px', color: '#065f46', fontSize: '1.6rem', fontWeight: '800' }}>
              Payment Successful!
            </h2>
            <p style={{ margin: '0 0 6px', color: '#10b981', fontSize: '2rem', fontWeight: '700' }}>
              ₹{fmtINR(paidAmount)}
            </p>
            <p style={{ margin: 0, color: '#6b7785', fontSize: '0.9rem' }}>
              Received from {patient.name}
            </p>
            <p style={{ margin: '4px 0 0', color: '#9aa5b1', fontSize: '0.82rem' }}>
              {modeLabels[payment.mode] || payment.mode} • {fmtDate(payment.date)}
            </p>
            {remaining > 0 && (
              <p style={{ margin: '12px 0 0', padding: '6px 16px', backgroundColor: '#fef3c7', borderRadius: '8px', color: '#92400e', fontSize: '0.85rem', fontWeight: '600' }}>
                Remaining Balance: ₹{fmtINR(remaining)}
              </p>
            )}
          </div>
        )}

        {/* Toolbar */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '14px 24px', borderBottom: '1px solid #eee', position: 'sticky', top: 0,
          backgroundColor: 'white', zIndex: 10, borderRadius: '12px 12px 0 0',
        }}>
          <h3 style={{ margin: 0, color: '#1a2332' }}>Payment Receipt</h3>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={handleConfirm}
              style={{ padding: '8px 22px', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700' }}>
              ✓ Confirm Received
            </button>
            <button onClick={handlePrint}
              style={{ padding: '8px 22px', backgroundColor: '#00B5B8', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700' }}>
              🖨 Print
            </button>
            <button onClick={onClose}
              style={{ padding: '8px 16px', backgroundColor: '#f0f0f0', color: '#555', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>
              Close
            </button>
          </div>
        </div>

        {/* Receipt content */}
        <div ref={printRef} style={{ padding: '24px' }}>
          <div className="receipt" style={{ maxWidth: '550px', margin: '0 auto', padding: '20px', border: '2px solid #333' }}>
            {/* Header */}
            <div style={{ textAlign: 'center', borderBottom: '2px solid #333', paddingBottom: '10px', marginBottom: '12px' }}>
              <div style={{ fontSize: '11px', letterSpacing: '2px', color: '#888', marginBottom: '4px' }}>PAYMENT RECEIPT</div>
              <div style={{ fontSize: '17px', fontWeight: 'bold', textTransform: 'uppercase' }}>
                {clinic?.name || 'SIDDHARTH DENTAL HOSPITAL'}
              </div>
              <div style={{ fontSize: '9px', color: '#555', marginTop: '2px' }}>
                {clinic?.address || ''} {clinic?.phone ? `| Ph: ${clinic.phone}` : ''}
              </div>
            </div>

            {/* Meta */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '11px' }}>
              <div>
                <div><strong>Receipt No:</strong> {payment.voucherNumber || '—'}</div>
                <div><strong>Date:</strong> {fmtDate(payment.date)}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div><strong>Patient:</strong> {patient.name}</div>
                {patient.uhid && <div><strong>UHID:</strong> {patient.uhid}</div>}
                {patient.phone && <div><strong>Phone:</strong> {patient.phone}</div>}
              </div>
            </div>

            {/* Payment details table */}
            <table style={{ width: '100%', borderCollapse: 'collapse', margin: '12px 0' }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5' }}>
                  <th style={{ border: '1px solid #ccc', padding: '6px 8px', fontSize: '10px', textTransform: 'uppercase', textAlign: 'left' }}>Description</th>
                  <th style={{ border: '1px solid #ccc', padding: '6px 8px', fontSize: '10px', textTransform: 'uppercase', textAlign: 'left' }}>Mode</th>
                  <th style={{ border: '1px solid #ccc', padding: '6px 8px', fontSize: '10px', textTransform: 'uppercase', textAlign: 'left' }}>Reference</th>
                  <th style={{ border: '1px solid #ccc', padding: '6px 8px', fontSize: '10px', textTransform: 'uppercase', textAlign: 'right' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ border: '1px solid #ccc', padding: '6px 8px' }}>Payment Received</td>
                  <td style={{ border: '1px solid #ccc', padding: '6px 8px' }}>{modeLabels[payment.mode] || payment.mode}</td>
                  <td style={{ border: '1px solid #ccc', padding: '6px 8px' }}>{payment.referenceNumber || '—'}</td>
                  <td style={{ border: '1px solid #ccc', padding: '6px 8px', textAlign: 'right', fontWeight: 'bold', fontSize: '13px' }}>₹{fmtINR(payment.amount)}</td>
                </tr>
              </tbody>
            </table>

            {/* Amount in words */}
            <div style={{ fontSize: '10px', border: '1px solid #ccc', padding: '5px 8px', marginBottom: '10px' }}>
              Amount Received (in words): <strong>INR {numberToWords(payment.amount)} Rupees Only</strong>
            </div>

            {/* Balance summary */}
            <div style={{ backgroundColor: '#f8f9fa', border: '1px solid #ddd', borderRadius: '6px', padding: '10px 14px', marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px' }}>
                <span>Total Billed:</span><span>₹{fmtINR(outstanding?.billed)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px', color: '#10b981' }}>
                <span>Total Paid:</span><span>₹{fmtINR(outstanding?.paid)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: 'bold', paddingTop: '6px', borderTop: '1px solid #ddd', color: remaining > 0 ? '#ef4444' : '#10b981' }}>
                <span>Balance Due:</span><span>₹{fmtINR(remaining)}</span>
              </div>
            </div>

            {payment.notes && (
              <div style={{ fontSize: '10px', color: '#555', marginBottom: '10px' }}>
                <strong>Notes:</strong> {payment.notes}
              </div>
            )}

            {/* Footer — QR + signature */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '20px' }}>
              {/* QR for this receipt amount */}
              <div id="receipt-qr-paid" style={{ textAlign: 'center' }}>
                {paidUpiUrl ? (
                  <>
                    <QRCodeSVG value={paidUpiUrl} size={80} level="M" />
                    <div style={{ fontSize: '8px', color: '#1a2332', marginTop: '3px', fontWeight: 'bold' }}>
                      ₹{fmtINR(paidAmount)} Received
                    </div>
                  </>
                ) : (
                  <div style={{ padding: '10px', fontSize: '11px', color: '#10b981', fontWeight: 'bold' }}>
                    ₹{fmtINR(paidAmount)} Received
                  </div>
                )}
              </div>

              {/* Signature */}
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '10px', marginBottom: '4px' }}>
                  For {clinic?.name || 'SIDDHARTH DENTAL HOSPITAL'}
                </div>
                <div style={{ borderTop: '1px solid #333', marginTop: '36px', paddingTop: '4px', fontSize: '9px', textAlign: 'center', width: '150px', marginLeft: 'auto' }}>
                  Authorized Signatory
                </div>
              </div>
            </div>

            <div style={{ textAlign: 'center', fontSize: '8px', color: '#aaa', marginTop: '14px' }}>
              This is a computer-generated receipt.
            </div>
          </div>
        </div>

        <style>{`
          @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
          @keyframes popIn { 0% { transform: scale(0); opacity: 0; } 60% { transform: scale(1.2); } 100% { transform: scale(1); opacity: 1; } }
        `}</style>
      </div>
    </div>
  );
};

export default PaymentReceipt;
